/* eslint-disable react/jsx-props-no-spreading */
import React from "react";

/**
 * A reusable modal component with a title, content area, and optional actions.
 * @param {object} props
 * @param {boolean} props.open - Controls the visibility of the modal.
 * @param {Function} props.onClose - Function to call when the modal is closed.
 * @param {string} props.title - The title of the modal.
 * @param {React.ReactNode} props.children - The content to display inside the modal.
 * @param {object} [props.primaryAction] - Configuration for the primary action button.
 * @param {string} [props.primaryAction.label] - The text for the primary button.
 * @param {Function} [props.primaryAction.onClick] - The click handler for the primary button.
 * @param {boolean} [props.primaryAction.loading] - Shows a loading state for the primary button.
 * @param {object} [props.secondaryAction] - Configuration for the secondary action button.
 * @param {string} [props.secondaryAction.label] - The text for the secondary button.
 * @param {Function} [props.secondaryAction.onClick] - The click handler for the secondary button.
 * @param {string} [props.maxWidth='sm'] - The maximum width of the modal. Can be 'sm', 'md', 'lg', etc.
 * @param {number} [props.zIndex=9999] - The z-index of the modal overlay.
 */
const CommonModal = ({
  open,
  onClose,
  title,
  children,
  primaryAction,
  secondaryAction,
  maxWidth = "sm",
  zIndex = 99999999999,
  ...props
}) => {
  // Map maxWidth string to Tailwind CSS classes
  const maxWidthClass =
    {
      sm: "max-w-sm",
      md: "max-w-md",
      lg: "max-w-lg",
      xl: "max-w-xl",
      "2xl": "max-w-2xl",
    }[maxWidth] || "max-w-md";

  if (!open) return null;

  return (
    // Modal Overlay
    <div
      className="fixed z-[99999999] inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 transition-opacity duration-300 ease-in-out"
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      {...props}
    >
      {/* Modal Content Wrapper */}
      <div
        className={`w-full ${maxWidthClass} transform rounded-lg bg-white p-0 shadow-xl transition-all duration-300 ease-in-out`}
      >
        {/* Modal Title */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 id="modal-title" className="text-xl font-semibold">
            {title}
          </h2>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600"
            onClick={onClose}
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="min-h-min p-6">{children}</div>

        {/* Modal Actions */}
        {(secondaryAction?.onClick || primaryAction?.onClick) && (
          <div className="flex justify-end gap-2 border-t bg-gray-50 px-6 py-4">
            {secondaryAction?.onClick && (
              <button
                type="button"
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                onClick={secondaryAction?.onClick}
              >
                {secondaryAction?.label || "Cancel"}
              </button>
            )}
            {primaryAction?.onClick && (
              <button
                type="button"
                className={`inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                  ${primaryAction?.loading && "cursor-not-allowed opacity-50"}`}
                disabled={primaryAction?.loading}
                onClick={primaryAction?.onClick}
              >
                {primaryAction?.loading && (
                  <svg
                    className="-ml-1 mr-3 h-5 w-5 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                {primaryAction?.label || "Submit"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommonModal;
